import { RequireAuth } from '@/components/require-auth'
import { AppLayout } from '@/components/app-layout'
import { AgencyService } from '@/services/agency-service'
import {
  attr,
  html,
  Fragment,
  OnDispose,
  prop,
  Signal,
  computedOf,
  When,
} from '@tempots/dom'
import { HTMLTitle, Query } from '@tempots/ui'
import {
  convertCustomizeToDesign,
  customizeAgencyContact,
  type CustomizeData,
} from '@rh/shared/templates/customize-to-design'
import { UserService } from '@/services/user-service'
import { sanitizeDesign } from '@rh/shared/utils/sanitize-design-html'
import { PAGE_SIZES, type SavedDesign } from '@rh/shared/schemas/design'
import {
  customizeNudgeDismissedKey,
  isUnconfiguredDesign,
  wizardDismissedKey,
} from './unconfigured-design'
import { createDesignEditorState, buildSavedDesign } from './design-state'
import { createDesignActions } from './design-actions'
import { PageCanvas } from './canvas'
import { ControlPanel, LeaveConfirmDialog, PageTabs } from './panels'
import { showOnboarding, type OnboardingActions } from './onboarding'
import { installUnsavedChangesGuard } from './unsaved-changes-guard'
import { WizardOverlay } from './wizard/wizard'
import './customize.css'

export interface CustomizePageOptions {
  agencyId: Signal<string>
}

/**
 * Resolves the design that should seed the editor: the agency's own saved
 * design if they have one, otherwise a design synthesized on the fly from
 * their legacy `customize` preferences (see convertCustomizeToDesign) — so
 * agencies who haven't touched the new editor yet see their existing
 * branding translated, not a blank template.
 */
function resolveInitialDesign(preferences: {
  design?: SavedDesign
  customize?: unknown
  headerLogoMeta?: { key: string }
  footerLogoMeta?: { key: string }
}): SavedDesign {
  if (preferences.design) return preferences.design
  return convertCustomizeToDesign(
    preferences.customize as CustomizeData | undefined,
    {
      headerLogoAssetKey: preferences.headerLogoMeta?.key,
      footerLogoAssetKey: preferences.footerLogoMeta?.key,
    }
  )
}

/**
 * WYSIWYG header/footer design editor — replaces the old toggle-based
 * Customize page. Persists a SavedDesign document via the same
 * PUT /agencies/:agencyId/preferences endpoint the old page used, under
 * the new `design` field.
 */
export function CustomizePage({ agencyId }: CustomizePageOptions) {
  return RequireAuth({}, () =>
    Query({
      request: null,
      load: () => AgencyService.getMyAgencyRole(agencyId.value),
      success: ({ value: role }) =>
        When(
          role.map(AgencyService.canManageTemplate),
          () => CustomizeEditor({ agencyId }),
          () =>
            AppLayout({
              agencyId,
              content: html.div(
                HTMLTitle('Customize - ResumeHeaders'),
                attr.class('p-4 sm:p-8 md:p-16 flex flex-col gap-2'),
                html.div(
                  attr.class('text-lg font-semibold text-gray-700'),
                  'The agency template is managed by your agency admins'
                ),
                html.div(
                  attr.class('text-gray-600'),
                  'Resumes you process automatically use the agency design. ' +
                    'Ask an agency admin or the owner if something in the ' +
                    'template needs to change.'
                )
              ),
            })
        ),
    })
  )
}

function CustomizeEditor({ agencyId }: CustomizePageOptions) {
  return Query({
    request: null,
    load: () => AgencyService.getAgency(agencyId.value),
    success: ({ value: agency }) => {
      const preferences = agency.$.preferences.value ?? {}
      const initialDesign = resolveInitialDesign(preferences)

      // They found the page — the workspace "start here" nudge is done.
      localStorage.setItem(customizeNudgeDismissedKey(agencyId.value), '1')

      const state = createDesignEditorState(initialDesign)
      const actions = createDesignActions(state)

      // Live token values for the canvas preview (unselected text blocks
      // render tokens resolved to the current user's data). Seeded from
      // the already-loaded preferences, hydrated with the recruiter
      // profile async, and refreshed after wizard write-backs.
      const refreshTokenData = async () => {
        try {
          const [prefs, profile] = await Promise.all([
            AgencyService.getAgencyPreferences(agencyId.value),
            UserService.getAgencyProfile(agencyId.value),
          ])
          const r = profile.recruiterProfile
          state.tokenData.set({
            agency: {
              name: agency.$.name.value,
              ...customizeAgencyContact(
                prefs.customize as CustomizeData | undefined
              ),
            },
            recruiter: {
              name: r?.name,
              phone: r?.phone,
              email: r?.email,
              linkedIn: r?.linkedIn,
            },
          })
        } catch {
          /* preview simply keeps showing chips */
        }
      }
      state.tokenData.set({
        agency: {
          name: agency.$.name.value,
          ...customizeAgencyContact(
            preferences.customize as CustomizeData | undefined
          ),
        },
        recruiter: {},
      })
      void refreshTokenData()

      const onboardingActions: OnboardingActions = {
        selectComponentByType: type => {
          const comps = state.components.value
          const match =
            type === 'any-layerable'
              ? comps.find(c => c.type !== 'resume' && c.type !== 'watermark')
              : comps.find(c => c.type === type)
          state.selectedId.set(match?.id ?? null)
        },
        deselectAll: () => state.selectedId.set(null),
      }

      // Setup wizard: auto-open for agencies that haven't made the design
      // their own (or via ?setup=1 deep link), unless previously
      // dismissed. While it's open the first-visit tour is suppressed —
      // it fires after the wizard closes instead, if still unseen.
      const forceSetup = new URLSearchParams(location.search).has('setup')
      const wizardOpen = prop(
        forceSetup ||
          (isUnconfiguredDesign(preferences) &&
            !localStorage.getItem(wizardDismissedKey(agencyId.value)))
      )
      // First-visit tour, matching the sandbox (no-op once dismissed).
      // Deferred a frame so the editor DOM the tour highlights exists.
      requestAnimationFrame(() => {
        if (!wizardOpen.value) showOnboarding(false, onboardingActions)
      })

      // Unsaved-changes tracking: the design serialized at load/last-save
      // time. Comparing serializations (rather than counting edits) means
      // undoing back to the saved state counts as clean again.
      let savedSnapshot = JSON.stringify(buildSavedDesign(state))
      const isDirty = () =>
        JSON.stringify(buildSavedDesign(state)) !== savedSnapshot

      const handleSave = async (): Promise<boolean> => {
        state.saveStatus.set('saving')
        const snapshot = JSON.stringify(buildSavedDesign(state))
        try {
          const design = sanitizeDesign(buildSavedDesign(state))
          await AgencyService.updateAgencyPreferences(agencyId.value, {
            design,
          })
          savedSnapshot = snapshot
          state.saveStatus.set('saved')
          return true
        } catch (error) {
          console.error('Failed to save design', error)
          state.saveStatus.set('error')
          return false
        }
      }

      // Leaving with unsaved changes prompts a save/discard/stay dialog.
      // `leaveAction` holds the blocked navigation while the dialog is up.
      const leaveAction = prop<(() => void) | null>(null)
      const disposeGuard = installUnsavedChangesGuard({
        isDirty,
        onBlocked: proceed => leaveAction.set(proceed),
      })

      // Anything that isn't selection chrome counts as "outside":
      // pressing there deselects. Chrome is the page canvas (it runs its
      // own empty-background deselection), overlays (wizard, leave
      // dialog, tour — the tour actively selects components to highlight
      // them), and the side panel's CONTROLS (e.g. "+ Text Block" selects
      // the block it adds; a bubbled deselect would clear it again). The
      // side panel's empty background deselects like anywhere else.
      const isSelectionChrome = (target: EventTarget | null) => {
        const t = target as HTMLElement | null
        if (!t?.closest) return false
        if (t.closest('.design-canvas-page')) return true
        if (
          t.closest(
            '.design-wizard-backdrop, .leave-dialog-backdrop, .ob-overlay, .ob-highlight'
          )
        )
          return true
        return Boolean(
          t.closest('.side-panel') &&
          t.closest('button, a, input, select, textarea, label')
        )
      }
      // Deselect only when the press also STARTED outside: a drag that
      // begins on a component and ends past the page edge fires its click
      // past the page, which would otherwise deselect mid-flow. Listeners
      // are document-level so the app header — outside this component's
      // DOM — counts as outside too.
      let pressStartedOutside = false
      const onDocMousedown = (e: MouseEvent) => {
        pressStartedOutside = !isSelectionChrome(e.target)
      }
      // click (not mousedown) so the editing overlay's blur commits
      // pending text before the selection is cleared.
      const onDocClick = (e: MouseEvent) => {
        if (!pressStartedOutside || isSelectionChrome(e.target)) return
        if (state.selectedId.value !== null) state.selectedId.set(null)
      }
      document.addEventListener('mousedown', onDocMousedown)
      document.addEventListener('click', onDocClick)
      const disposeDeselect = () => {
        document.removeEventListener('mousedown', onDocMousedown)
        document.removeEventListener('click', onDocClick)
      }

      return Fragment(
        HTMLTitle('Customize - ResumeHeaders'),
        OnDispose(disposeGuard),
        OnDispose(disposeDeselect),
        AppLayout({
          agencyId,
          // The canvas area scrolls on both axes; the side panel sits
          // outside the scroller as a full-height column, so the editor
          // controls stay visible while scrolling to the bottom of the
          // page (or sideways on narrow windows). The page keeps its
          // natural width (no shrinking — a scaled-down page isn't
          // editable).
          content: html.div(
            attr.class('design-editor'),
            attr.style(
              'height:100%; display:flex; overflow:hidden; background:#f9fafb;'
            ),
            // Canvas region: relative wrapper so the selection tips card
            // can float over the bottom-left without scrolling away.
            html.div(
              attr.style(
                'flex:1; min-width:0; position:relative; overflow:hidden;'
              ),
              html.div(
                attr.style(
                  'height:100%; overflow:auto; padding:16px; box-sizing:border-box;'
                ),
                // width:max-content + margin:auto centers the page when
                // there's room and pins it left (scrollable) when there
                // isn't — justify-content:center would clip the left edge
                // once the content overflows.
                html.div(
                  attr.style(
                    computedOf(state.pageSize)(
                      size => `width:${PAGE_SIZES[size].w}px; margin:0 auto;`
                    )
                  ),
                  PageTabs(state, actions),
                  PageCanvas({
                    state,
                    actions,
                    agencyId: agencyId.value,
                  })
                )
              )
            ),
            html.div(
              attr.class('side-panel'),
              ControlPanel({
                state,
                actions,
                onSave: handleSave,
                onTour: () => showOnboarding(true, onboardingActions),
                onOpenWizard: () => wizardOpen.set(true),
              })
            ),
            WizardOverlay({
              open: wizardOpen,
              agencyId: agencyId.value,
              agencyName: agency.$.name.value,
              preferences,
              replacesExistingDesign: !isUnconfiguredDesign(preferences),
              onComplete: design => {
                actions.replaceAll(sanitizeDesign(design))
                // The wizard may have just written profile values the
                // canvas preview resolves from.
                void refreshTokenData()
                // The wizard produced a design worth keeping around; the
                // auto-open shouldn't fire again next visit even if the
                // user leaves without saving.
                localStorage.setItem(wizardDismissedKey(agencyId.value), '1')
                requestAnimationFrame(() =>
                  showOnboarding(false, onboardingActions)
                )
              },
              onDismiss: () => {
                localStorage.setItem(wizardDismissedKey(agencyId.value), '1')
                requestAnimationFrame(() =>
                  showOnboarding(false, onboardingActions)
                )
              },
            }),
            LeaveConfirmDialog({
              open: leaveAction.map(a => a != null),
              saving: state.saveStatus.map(s => s === 'saving'),
              onSaveAndLeave: async () => {
                const proceed = leaveAction.value
                if (await handleSave()) {
                  leaveAction.set(null)
                  proceed?.()
                }
                // On failure the dialog stays open; the save bar shows
                // the error toast.
              },
              onLeave: () => {
                const proceed = leaveAction.value
                leaveAction.set(null)
                proceed?.()
              },
              onStay: () => leaveAction.set(null),
            })
          ),
        })
      )
    },
  })
}
